export type BarMenuCategory = 'breakfast' | 'lunch' | 'aperitifs'

export interface BarMenuItem {
  id?: string
  name: string
  description?: string
  allergens?: string
  price?: string
  image: string
}

export interface StoredBarMenuItem extends BarMenuItem {
  id: string
  category: BarMenuCategory
  createdAt: number
  updatedAt?: number
}

function storageKey(category: BarMenuCategory): string {
  return `bar-${category}`
}

function seededKey(category: BarMenuCategory): string {
  return `bar-${category}-seeded`
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function readItems(category: BarMenuCategory): StoredBarMenuItem[] {
  try {
    const raw = localStorage.getItem(storageKey(category))
    return raw ? (JSON.parse(raw) as StoredBarMenuItem[]) : []
  } catch {
    return []
  }
}

function writeItems(category: BarMenuCategory, items: StoredBarMenuItem[]): void {
  localStorage.setItem(storageKey(category), JSON.stringify(items))
}

export function getBarMenuItems(category: BarMenuCategory): StoredBarMenuItem[] {
  return readItems(category)
}

export function setBarMenuItems(category: BarMenuCategory, items: StoredBarMenuItem[]): void {
  const now = Date.now()
  writeItems(
    category,
    items.map((item) => ({
      ...item,
      updatedAt: now,
    }))
  )
}

export function seedBarMenuItems(
  category: BarMenuCategory,
  fallback: BarMenuItem[]
): void {
  try {
    const alreadySeeded = localStorage.getItem(seededKey(category)) === '1'
    if (alreadySeeded) return

    const items = readItems(category)
    if (items.length > 0) {
      localStorage.setItem(seededKey(category), '1')
      return
    }

    const now = Date.now()
    const seeded: StoredBarMenuItem[] = fallback.map((item) => ({
      id: generateId(),
      category,
      name: item.name,
      description: item.description,
      allergens: item.allergens,
      price: item.price,
      image: item.image,
      createdAt: now,
    }))

    writeItems(category, seeded)
    localStorage.setItem(seededKey(category), '1')
  } catch {
    // ignore seeding errors
  }
}

export function getStoredBarItemsOrFallback(
  category: BarMenuCategory,
  fallback: BarMenuItem[]
): BarMenuItem[] {
  const items = readItems(category)
  if (items.length === 0) return fallback

  return items.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description,
    allergens: item.allergens,
    price: item.price,
    image: item.image,
  }))
}
